import type { HTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { CopyButton } from './CopyButton';

export interface MonoProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The identifier. `null` renders an em dash rather than an empty cell. */
  value: string | null | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Adds a copy control that appears on hover and on keyboard focus. */
  copyable?: boolean;
  /** What is being copied, for the control's label: "chassis number", "VIN". */
  label?: string;
  /** Emphasises the last n characters — how depot staff actually read a chassis number. */
  highlightTail?: number;
  /** Dims it to a secondary identifier. */
  subdued?: boolean;
  truncate?: boolean;
}

const SIZES = {
  xs: 'text-label',
  sm: 'text-micro',
  md: 'text-micro',
  lg: 'text-body',
} as const;

/**
 * A stamped identifier — chassis number, VIN, invoice number.
 *
 * This is the detail that makes the product look like it belongs to vehicles: the
 * mono face, slight letter-spacing and tabular figures so a column of chassis numbers
 * lines up character for character and a transposed digit is visible at a glance.
 */
export function Mono({
  value,
  size = 'md',
  copyable = false,
  label = 'identifier',
  highlightTail,
  subdued = false,
  truncate = false,
  className,
  ...rest
}: MonoProps) {
  if (value === null || value === undefined || value === '') {
    return (
      <span className={clsx('font-data text-faint', SIZES[size], className)} {...rest}>
        —
      </span>
    );
  }

  const tail = highlightTail && highlightTail > 0 ? value.slice(-highlightTail) : null;
  const head = tail ? value.slice(0, value.length - tail.length) : value;

  return (
    <span className={clsx('group/mono inline-flex min-w-0 items-center gap-1', className)} {...rest}>
      <span
        className={clsx(
          'font-data tracking-[0.02em] tabular-nums',
          SIZES[size],
          subdued ? 'text-muted' : 'text-ink',
          truncate && 'truncate',
        )}
        title={value}
      >
        {tail ? (
          <>
            <span className="text-muted">{head}</span>
            <span className={clsx('font-semibold', subdued ? 'text-ink-2' : 'text-ink')}>
              {tail}
            </span>
          </>
        ) : (
          value
        )}
      </span>
      {copyable ? (
        <CopyButton
          value={value}
          label={label}
          className="opacity-0 transition-opacity duration-150 group-hover/mono:opacity-100 focus-visible:opacity-100"
        />
      ) : null}
    </span>
  );
}
